import { useCallback } from "react";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

export const AHS_SCREEN_IDS = {
  TRANSFER: "AHS_INVENTORY_TRANSFER",
  AUDIT_TRAIL: "AHS_AUDIT_TRAIL",
} as const;

export const AHS_CLICK_EVENTS = {
  /* Transfer listing screen */
  DIRECTION_TAB_CLICK: "AHS_DIRECTION_TAB_CLICK",
  CREATE_TRANSFER_CLICK: "AHS_CREATE_TRANSFER_CLICK",
  FILTER_APPLY_CLICK: "AHS_FILTER_APPLY_CLICK",
  ITR_SEARCH_CLICK: "AHS_ITR_SEARCH_CLICK",
  VIEW_ITEMS_CLICK: "AHS_VIEW_ITEMS_CLICK",
  EDIT_REQUEST_CLICK: "AHS_EDIT_REQUEST_CLICK",
  CANCEL_REQUEST_CLICK: "AHS_CANCEL_REQUEST_CLICK",
  ROW_ACTION_CLICK: "AHS_ROW_ACTION_CLICK",
  /* Cancel request modal */
  CANCEL_REQUEST_CONFIRM_CLICK: "AHS_CANCEL_REQUEST_CONFIRM_CLICK",
  /* Create / edit transfer screen */
  SAVE_DRAFT_CLICK: "AHS_SAVE_DRAFT_CLICK",
  CREATE_TRANSFER_REVIEW_CLICK: "AHS_CREATE_TRANSFER_REVIEW_CLICK",
  CREATE_TRANSFER_CONFIRM_CLICK: "AHS_CREATE_TRANSFER_CONFIRM_CLICK",
  /* View items modal (accept / reject) */
  ACCEPT_REQUEST_CLICK: "AHS_ACCEPT_REQUEST_CLICK",
  REJECT_REQUEST_CLICK: "AHS_REJECT_REQUEST_CLICK",
  REQUEST_ACTION_CONFIRM_CLICK: "AHS_REQUEST_ACTION_CONFIRM_CLICK",
  /* Audit trail */
  AUDIT_TRAIL_APPLY_FILTERS_CLICK: "AHS_AUDIT_TRAIL_APPLY_FILTERS_CLICK",
  AUDIT_TRAIL_EXPORT_CSV_CLICK: "AHS_AUDIT_TRAIL_EXPORT_CSV_CLICK",
} as const;

export type AhsClickEvent = (typeof AHS_CLICK_EVENTS)[keyof typeof AHS_CLICK_EVENTS];

/**
 * Click-log helper for the AHS inventory flow. Auto-fills scr_id, clinic,
 * vms_user_id and timestamp so call sites only pass the event-specific fields.
 */
export const useAhsInventoryClickLog = (scrId: string = AHS_SCREEN_IDS.TRANSFER) => {
  const { execute: logAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const logAhsEvent = useCallback(
    (eventType: AhsClickEvent, extra: Partial<IClickLogBody> = {}) => {
      const logBody: IClickLogBody = {
        event_type: eventType,
        scr_id: scrId,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        vms_user_id: userLogInfo,
        timestamp: Date.now(),
        ...extra,
      };
      logAPI(logBody);
    },
    [logAPI, scrId, mappedClinicId, mappedClinicName, userLogInfo],
  );

  return { logAhsEvent };
};
